import clsx from "clsx";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "web/react/emo/button";
import { Heading } from "web/react/emo/heading";
import { Text } from "web/react/emo/text";
import { VStack } from "web/react/emo/v-stack";
import { View } from "web/react/emo/view";
import { useAboveFold } from "web/react/hooks/use-above-fold/use-above-fold";
import analytics from "web/script/analytics/analytics";
import { generateEventData } from "web/script/analytics/utils";
import { gettext } from "web/script/modules/django-i18n";
import environment from "web/script/modules/environment";
import rothko from "web/script/modules/rothko";
import { useSendImpression } from "web/script/utils/ads";
import uuid4 from "web/script/utils/uuid";
import { BrandAdSerializer } from "web/types/serializers";
import * as styles from "./brand-ad-container.css";

const ENDPOINT = "modules/brand_ads";

export type BrandAdEvent = {
    action: "loaded" | "click" | "impression";
    area: string;
    placement: Placement | null;
    retailerNetwork: BrandAdSerializer["retailer_network"];
    campaignId: BrandAdSerializer["campaign_id"];
    flightId: BrandAdSerializer["flight_id"];
    adId: BrandAdSerializer["ad_id"];
    creativeId: BrandAdSerializer["creative_id"];
    isAboveFold: boolean;
    isInternalAd: BrandAdSerializer["is_internal"];
    kevelEventId?: string;
};

type Placement = {
    name: BrandAdSerializer["placement_name"] | null;
    "co-ordinates": {
        "x-axis": number | null;
        "y-axis": number | null;
    };
    dimension: {
        width: number | null;
        height: number | null;
    };
    viewport: {
        width: number | null;
        height: number | null;
    };
};

type AgifResponse = "load" | "error" | "timeout";

function sendBeaconEvent(): boolean {
    const eventData = generateEventData(
        "brand_ads_beacon", // category
        "test", // action
        "", // label
        true // nonInteraction
    );
    const data = { events: [analytics.addMetaData(eventData)] };
    const agifTransportUrl = environment.get("agifAnalyticsUrl");
    return !!navigator.sendBeacon && navigator.sendBeacon(agifTransportUrl, JSON.stringify(data));
}

export function getPlacement(
    element: HTMLDivElement,
    placementName: BrandAdSerializer["placement_name"]
): Placement {
    const coordinates: Placement["co-ordinates"] = {
        "x-axis": null,
        "y-axis": null,
    };

    const dimension: Placement["dimension"] = {
        width: null,
        height: null,
    };

    const viewport: Placement["viewport"] = {
        width: null,
        height: null,
    };

    if (window) {
        const domReact = element.getBoundingClientRect();
        const documentEl = document.documentElement.getBoundingClientRect();

        coordinates["y-axis"] = domReact.top - documentEl.top;
        coordinates["x-axis"] = domReact.x;

        dimension["height"] = domReact.height;
        dimension["width"] = domReact.width;

        viewport["height"] = window.innerHeight;
        viewport["width"] = window.innerWidth;
    }

    return {
        name: placementName || null,
        "co-ordinates": coordinates,
        dimension,
        viewport,
    };
}

export function sendEvent({
    action,
    area,
    retailerNetwork,
    campaignId,
    flightId,
    creativeId,
    isAboveFold,
    isInternalAd,
    adId,
    placement = null,
    kevelEventId,
}: BrandAdEvent): void {
    analytics.event({
        category: "brand_ads",
        action,
        label: "",
        nonInteraction: action !== "click",
        customData: {},
        subType: "",
        checkoutData: {},
        itemData: [
            {
                item_type: "ad_brand",
                retailer_network: retailerNetwork,
                campaign_id: String(campaignId),
                flight_id: String(flightId),
                creative_id: String(creativeId),
                ad_id: String(adId),
                area,
                above_fold: isAboveFold,
                internal: isInternalAd,
                placement,
                ...(kevelEventId && { kevel_event_id: kevelEventId }),
            },
        ],
    });
}

export interface BrandAdContainerProps extends BrandAdSerializer {
    onOverlay?: boolean;
}

export function BrandAdContainer({
    desktop_asset_url: desktopAssetUrl,
    mobile_asset_url: mobileAssetUrl,
    link,
    click_url: clickUrl,
    impression_url: impressionUrl,
    partner,
    retailer_network: retailerNetwork,
    campaign_id: campaignId,
    flight_id: flightId,
    creative_id: creativeId,
    ad_id: adId,
    is_internal: isInternalAd,
    placement_name: placementName,
    onOverlay = false,
    title,
    caption,
    button_cta: buttonCta,
}: BrandAdContainerProps): null | JSX.Element {
    const containerRef = useRef<HTMLDivElement>(null);

    const isDeepAffiliatePhase1Enabled = environment.getFeature(
        "deep_affiliate_experiment_phase_1"
    );
    const { isAboveFold, isVisible } = useAboveFold(containerRef);
    const [isLoaded, setIsLoaded] = useState(false);
    const [isSuccessAGifResponse, setIsSuccessAGifResponse] = useState<AgifResponse | null>(null);

    const { shouldSendImpression, setImpressionSent } = useSendImpression(isVisible, onOverlay);

    const validateAGifResponse = useCallback(async () => {
        if (isSuccessAGifResponse !== null) {
            return isSuccessAGifResponse;
        }

        let timeoutId: ReturnType<typeof setTimeout>;
        const img = document.createElement("img");

        const promise = new Promise<string>((resolve) => {
            // We create an image element and set its src to the agif url (just
            // like how we load the actual analytics gif). This triggers a
            // request to that url. We then listen for either a load or error
            // event, or a timeout. the handler for any of thse sets the
            // response value, clears all the other handlers and finally
            // resolves the promise.

            function done(result: AgifResponse): void {
                clearTimeout(timeoutId);
                img.onload = () => {};
                img.onerror = () => {};
                img.src = "";
                setIsSuccessAGifResponse(result);
                resolve(result);
            }

            // this is how long we wait for requests when sending actual data
            // to the agif in `agif-transport.js`
            timeoutId = setTimeout(() => done("timeout"), 2000);

            img.onload = () => done("load");
            img.onerror = () => done("error");
            // calls to the bare gif seem to get cached, so let's add a unique query param
            img.src = environment.get("agifAnalyticsUrl") + "?test=" + uuid4.uuid4();
        });
        return await promise;
    }, [isSuccessAGifResponse]);

    const sendKevelTracking = useCallback(
        async (isClick: boolean, kevelEventId?: string): Promise<void> => {
            const type = "pba";
            const url = isClick ? clickUrl : impressionUrl;

            const aGifResponse = await validateAGifResponse();

            if (url) {
                const clicked = isClick ? "true" : "false";
                const supportsBeacon = !!navigator.sendBeacon;
                rothko.fetch(
                    ENDPOINT,
                    {
                        url,
                        clicked,
                        type,
                        agif_response: aGifResponse,
                        supports_beacon: supportsBeacon.toString(),
                        // also send a test even using sendBeacon to compare results
                        beacon_queued: sendBeaconEvent().toString(),
                        ...(kevelEventId && { kevel_event_id: kevelEventId }),
                    },
                    { cache: false }
                );
            }
        },
        [clickUrl, impressionUrl, validateAGifResponse]
    );

    const sendAnalyticsEvent = useCallback(
        (action, area, placementName?, kevelEventId?) => {
            const placement =
                containerRef.current && getPlacement(containerRef.current, placementName);
            sendEvent({
                action,
                area,
                retailerNetwork,
                campaignId,
                flightId,
                creativeId,
                isAboveFold,
                isInternalAd,
                adId,
                placement,
                kevelEventId,
            });
        },

        [
            retailerNetwork,
            campaignId,
            flightId,
            creativeId,
            adId,
            isAboveFold,
            isInternalAd,
            placementName,
        ]
    );

    function onMainButtonClick(): void {
        const kevelEventId = uuid4.uuid4();
        sendKevelTracking(true, kevelEventId);
        sendAnalyticsEvent("click", "banner", placementName, kevelEventId);
    }

    function onTextAreaClick(): void {
        const kevelEventId = uuid4.uuid4();
        sendKevelTracking(true, kevelEventId);
        sendAnalyticsEvent("click", "title & caption", undefined, kevelEventId);
    }

    function onShopNowBtnClick(): void {
        const kevelEventId = uuid4.uuid4();
        sendKevelTracking(true, kevelEventId);
        sendAnalyticsEvent("click", "call to action", undefined, kevelEventId);
    }

    // "Curated by Lyst" : "Sponsored by {partner}"
    const heading = isInternalAd
        ? gettext("curated_ads.curated_by_lyst")
        : gettext("in_stock_product.sponsored_by_retailer", { retailer_name: partner });

    useEffect(() => {
        if (!isLoaded) {
            sendAnalyticsEvent("loaded", "no interaction", placementName);
            setIsLoaded(true);
        }
    }, [isLoaded, sendAnalyticsEvent, placementName]);

    useEffect(() => {
        if (shouldSendImpression) {
            const kevelEventId = uuid4.uuid4();
            sendKevelTracking(false, kevelEventId);
            setImpressionSent(true);
            sendAnalyticsEvent("impression", "no interaction", placementName, kevelEventId);
        }
    }, [
        placementName,
        sendKevelTracking,
        sendAnalyticsEvent,
        setImpressionSent,
        shouldSendImpression,
    ]);

    return (
        <div ref={containerRef} className={clsx(styles.container)}>
            <div className={styles.partnerAdsLeftArea}>
                <a
                    href={link}
                    target="_blank"
                    onClick={onTextAreaClick}
                    rel="noreferrer"
                    className={styles.partnerAds}
                >
                    <VStack spacing="xs">
                        <Text textStyle="body-xs-bold" color="secondaryBrand" upperCase>
                            {heading}
                        </Text>
                        <Heading textStyle="title-2" as="h3">
                            {title}
                        </Heading>
                        <Text textStyle="body-s" color="secondary">
                            {caption}
                        </Text>
                    </VStack>
                </a>
                <a
                    href={link}
                    target="_blank"
                    onClick={onShopNowBtnClick}
                    rel="noreferrer"
                    className={styles.partnerAdsButtonContainer}
                >
                    {isDeepAffiliatePhase1Enabled ? (
                        <Button
                            title={buttonCta}
                            variant="secondary"
                            icon="external-link"
                            iconPosition="right"
                        />
                    ) : (
                        <Button title={buttonCta} variant="secondary" />
                    )}
                </a>
            </div>
            <View className={styles.bannerContainer}>
                <a
                    href={link}
                    className={styles.bannerContainerInner}
                    target="_blank"
                    onClick={onMainButtonClick}
                    rel="noreferrer"
                >
                    {/*
                        Note for future changes: If something regarding the media queries
                        in images load or the picture sources change please update `base_page.jinja`
                        as well since we are preloading them conditionally.
                    */}
                    <picture>
                        <source media="(max-width: 575px)" srcSet={mobileAssetUrl} />
                        <source media="(min-width: 576px)" srcSet={desktopAssetUrl} />
                        <img
                            className={clsx(styles.containerImage)}
                            src={desktopAssetUrl}
                            alt={`${partner} banner`}
                        />
                    </picture>
                </a>
            </View>
        </div>
    );
}
